import { reactive } from 'vue'
import { normalizePalette, applyPalette } from './corporatePalette'
import legacyContentBlocks from '@/data/corporateContentBlocks.json'

// Local requests use the development server proxy, regardless of its chosen port.
export const CORPORATE_API = process.env.NODE_ENV === 'development'
  ? ''
  : (process.env.VUE_APP_LENDIQ_API_URL || 'https://api.lendiq.app').replace(/\/$/, '')
export const siteIdentity = reactive({})
export const defaultSettings = Object.freeze({
  display_name: process.env.VUE_APP_COMPANY_DISPLAY_NAME,
  legal_name: process.env.VUE_APP_COMPANY_LEGAL_NAME,
  full_legal_name: process.env.VUE_APP_COMPANY_FULL_LEGAL_NAME,
  registration_certificate: process.env.VUE_APP_COMPANY_REGISTRATION_CERTIFICATE,
  footer_license: process.env.VUE_APP_COMPANY_FOOTER_LICENSE,
  header_logo_url: process.env.VUE_APP_COMPANY_HEADER_LOGO_URL,
  footer_logo_url: process.env.VUE_APP_COMPANY_FOOTER_LOGO_URL,
  revision: null,
  content_blocks: legacyContentBlocks
})
export const siteAvailability = reactive({ maintenance: false, refreshing: false })
// Vue CLI environment variables are strings; only the literal true enables fallback.
const settingsFallbackEnabled = process.env.VUE_APP_SETTINGS_FALLBACK_ENABLED?.trim().toLowerCase() === 'true'
let inflight

export async function corporateRequest(path, signal) {
  const response = await fetch(`${CORPORATE_API}/api/v1/corporate-site${path}`, { signal, credentials: 'omit', cache: 'no-store' })
  if (!response.ok) {
    const error = new Error(response.status === 404 ? 'Матеріал не знайдено' : 'Не вдалося завантажити дані. Спробуйте ще раз.')
    error.status = response.status
    throw error
  }
  const data = await response.json()
  if (path.startsWith('/news') || path.startsWith('/notices')) {
    const resolve = item => ({ ...item, image_url: CORPORATE_API && item.image_url?.startsWith('/api/') ? new URL(item.image_url, CORPORATE_API).href : item.image_url })
    return Array.isArray(data) ? data.map(resolve) : resolve(data)
  }
  return data
}

// Public news addresses retain the UUID fallback for older API deployments and links.
export function corporateNewsPath(news) {
  const id = news.public_id
  const segment = Number.isInteger(id) && id >= 100000 && id <= 999999
    ? `${id}_${typeof news.slug === 'string' ? news.slug : ''}`
    : news.uuid
  return `/news/${encodeURIComponent(segment)}`
}

export function corporateNoticeLink(value) {
  if (typeof value !== 'string') return null
  if (Array.from(value).some(char => {
    const code = char.charCodeAt(0)
    return char === '\\' || code <= 31 || (code >= 127 && code <= 159)
  })) return null
  if ((value.startsWith('#') && value.length > 1) || (value.startsWith('/') && !value.startsWith('//'))) return value
  try {
    const url = new URL(value)
    if (url.protocol === 'https:' && !url.username && !url.password) return value
  } catch (_) { /* Ignore invalid links. */ }
  return null
}

// Guard the display period locally as well: an open tab must drop a notice at its end time.
export function activeCorporateNotices(notices, now = Date.now()) {
  return notices.filter(notice => {
    if (!notice || notice.hidden || typeof notice.uuid !== 'string') return false
    const starts = Date.parse(notice.starts_at), ends = Date.parse(notice.ends_at)
    return Number.isFinite(starts) && Number.isFinite(ends) && starts <= now && now < ends &&
      [notice.title, notice.text, notice.image_url].some(value => typeof value === 'string' && value.trim())
  }).map(notice => ({ ...notice,
    kind: ['information', 'important', 'critical', 'celebration'].includes(notice.kind) ? notice.kind : 'information',
    link_url: corporateNoticeLink(notice.link_url)
  })).sort((a, b) => (a.position || 0) - (b.position || 0) || Date.parse(a.starts_at) - Date.parse(b.starts_at) || a.uuid.localeCompare(b.uuid))
}

function preloadLogo(url) {
  return new Promise((resolve, reject) => {
    const image = new Image()
    const timeout = setTimeout(() => reject(new Error('Не вдалося завантажити логотип')), 6000)
    image.onload = () => { clearTimeout(timeout); resolve() }
    image.onerror = () => { clearTimeout(timeout); reject(new Error('Не вдалося завантажити логотип')) }
    image.src = url
  })
}

export function refreshIdentity() {
  if (inflight) return inflight
  siteAvailability.refreshing = true
  inflight = (async () => {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 7000)
    let settingsReceived = false
    try {
      const next = await corporateRequest('/settings', controller.signal)
      if (!['display_name', 'legal_name', 'full_legal_name', 'header_logo_url', 'footer_logo_url'].every(k => typeof next[k] === 'string' && next[k].trim())) throw new Error('Некоректні налаштування сайту')
      const palette = normalizePalette(next.palette)
      // An API deployed before editable blocks has no such field. Only that version
      // uses the bundled legacy snapshot. Explicit [] and hidden API slots stay empty.
      const content_blocks = next.content_blocks === undefined ? legacyContentBlocks : next.content_blocks
      if (!Array.isArray(content_blocks) || !content_blocks.every(b => b && typeof b.key === 'string' && typeof b.title === 'string' && typeof b.rendered_html === 'string' && typeof b.hidden === 'boolean')) throw new Error('Некоректні текстові блоки сайту')
      settingsReceived = true
      // A backend upgrade can add the field without changing the stored settings revision.
      if (siteIdentity.revision === next.revision && JSON.stringify(siteIdentity.content_blocks) === JSON.stringify(content_blocks)) {
        siteAvailability.maintenance = false
        return
      }
      const logos = ['header_logo_url', 'footer_logo_url'].filter(k => next[k] !== siteIdentity[k]).map(k => preloadLogo(next[k]))
      // Initial text can render even if the image host is temporarily unavailable.
      // Subsequent changes wait for both new logos so the visible identity changes together.
      if (siteIdentity.revision === undefined) await Promise.allSettled(logos)
      else await Promise.all(logos)
      applyPalette(palette)
      Object.assign(siteIdentity, next, { palette, content_blocks })
      siteAvailability.maintenance = false
    } catch (error) {
      console.error('Не вдалося завантажити налаштування сайту:', error)
      // A logo-host failure is not an unavailable settings endpoint.
      if (settingsReceived) { siteAvailability.maintenance = false; throw error }
      if (!settingsFallbackEnabled) {
        siteAvailability.maintenance = true
        throw error
      }
      // Bootstrap must still mount during an outage. Once API settings have been
      // applied, retain them on transient failures to avoid reverting a renamed company.
      if (siteIdentity.revision !== undefined) throw error
      const palette = normalizePalette()
      applyPalette(palette)
      Object.assign(siteIdentity, defaultSettings, { palette })
    } finally { clearTimeout(timeout); siteAvailability.refreshing = false }
  })().finally(() => { inflight = null })
  return inflight
}

const escapeHtml = value => String(value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
export function companyText(text, html = false) {
  const name = key => {
    const value = siteIdentity[key] ?? defaultSettings[key] ?? ''
    return html ? escapeHtml(value) : value
  }
  // One replacement pass prevents values supplied by the API from being rewritten again.
  const pattern = /\{\{company\.(display_name|legal_name|full_legal_name|registration_certificate|footer_license)\}\}|ТОВАРИСТВО З ОБМЕЖЕНОЮ ВІДПОВІДАЛЬНІСТЮ\s*[«“"]СУЧАСНИЙ ФАКТОРИНГ[»”"]|ТОВ\s*[«“"]СУЧАСНИЙ ФАКТОРИНГ[»”"]|ТОВ Сучасного факторингу|[«“"]Сучасному Факторингу[»”"]|[«“"]Сучасний Факторинг[»”"]|Сучасного Факторингу|Сучасний Факторинг/gi
  return String(text || '').replace(/\{\{site\.current_year\}\}/g, String(new Date().getFullYear())).replace(pattern, (match, key) => name(key || (match.toUpperCase().startsWith('ТОВАРИСТВО') ? 'full_legal_name' : match.toUpperCase().startsWith('ТОВ') ? 'legal_name' : 'display_name')))
}

// API rich text is already sanitized by the server; legacy HTML is a reviewed bundled snapshot. Identity substitution touches text nodes only,
// so a company name can never rewrite link/image attributes into an executable URL.
export function companyHtml(html) {
  const document = new DOMParser().parseFromString(String(html || ''), 'text/html')
  const walker = document.createTreeWalker(document.body, 4)
  let node
  while ((node = walker.nextNode())) node.nodeValue = companyText(node.nodeValue)
  return document.body.innerHTML
}

// Layout stays in Vue; editable, server-sanitized content arrives with the initial settings.
export function siteBlocks(keys) {
  return keys.map(key => (siteIdentity.content_blocks || []).find(b => b.key === key)).filter(b => b && !b.hidden)
}
